/**
 * Would this track actually DRIVE something on this node at playback?
 *
 * A name-only check over-counts: a morph track whose empty node name
 * resolves to the root "binds" by name yet is inert because the root
 * carries no morphTargetInfluences (Codex + review, PR #16); a material
 * path binds to nothing the mixer can sample. One rule, every door that
 * counts bindings: the viewport's present and the library binder (Codex
 * P2, #151 round three — the binder had re-implemented the name half and
 * skipped the property half, so a clip the viewport would refuse could be
 * cached with a reported hit rate).
 *
 * Depends on: three. Depended on by: library-clip, the app's viewport.
 */
import { PropertyBinding, type Object3D } from 'three';

export function resolvesProperty(node: Object3D, propertyName: string, propertyIndex: string | undefined): boolean {
  if (propertyName === 'quaternion' || propertyName === 'position' || propertyName === 'scale') {
    return true; // every Object3D carries the TRS properties
  }
  if (propertyName === 'morphTargetInfluences') {
    const mesh = node as { morphTargetInfluences?: number[]; morphTargetDictionary?: Record<string, number> };
    if (!Array.isArray(mesh.morphTargetInfluences)) return false;
    if (propertyIndex === undefined) return true; // whole-array track
    if (mesh.morphTargetDictionary && propertyIndex in mesh.morphTargetDictionary) return true;
    const index = Number(propertyIndex);
    return Number.isInteger(index) && index >= 0 && index < mesh.morphTargetInfluences.length;
  }
  return false; // anything else (material paths etc.) is not viewport-playable content
}

export interface ParsedTrackName {
  readonly nodeName: string | undefined;
  readonly propertyName: string;
  readonly propertyIndex: string | undefined;
}

export function parseTrackName(trackName: string): ParsedTrackName {
  const parsed = PropertyBinding.parseTrackName(trackName) as { nodeName?: string; propertyName: string; propertyIndex?: string };
  return { nodeName: parsed.nodeName, propertyName: parsed.propertyName, propertyIndex: parsed.propertyIndex };
}

/** The node a track name reaches under `root` (three's rule: no node name = the root), and whether its property resolves there. */
export function trackBindsOn(root: Object3D, trackName: string): boolean {
  const { nodeName, propertyName, propertyIndex } = parseTrackName(trackName);
  const node = PropertyBinding.findNode(root, nodeName as string) as Object3D | null;
  return node !== null && resolvesProperty(node, propertyName, propertyIndex);
}
